import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { green } from '@material-ui/core/colors';
import { STATUS_API } from 'src/app/constant/config';
import * as Yup from 'yup';
import { Formik } from 'formik';
import moment from 'moment';
import { createDevice, importDevice, resetResultImport } from 'src/features/deviceSlice';
import CustomDialog from 'src/app/components/CustomDialog';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const initStateFromValue = {
  serial: '',
  device_type_id: '',
  hardware_version: '',
  date_of_manufacture: moment().format('YYYY-MM-DDTHH:mm')
};

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`create-device-tabpanel-${index}`}
      aria-labelledby={`create-device-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `create-device-tab-${index}`,
    'aria-controls': `create-device-tabpanel-${index}`
  };
}

const CreateDevice = React.memo(({ open, onClose, getListVersion }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const statusCreate = useSelector(state => state.staffSlice.statusCreate);
  const listDeviceVersion = useSelector(
    state => state.versionDeviceSlice.listDeviceVersion
  );
  const listDeviceType = useSelector(
    state => state.deviceTypeSlice.listDeviceType
  );
  const resultImport = useSelector(state => state.deviceSlice.resultImport);

  const deviceType = useMemo(
    () => [
      ...listDeviceType.map(type => ({
        id: type.id,
        name: type.name
      }))
    ],
    [listDeviceType]
  );

  const versionOption = listDeviceVersion?.map(item => {
    return {
      id: item.id,
      name: item.version
    };
  });

  const [initValue, setInitValue] = useState(initStateFromValue);
  const [tabValue, setTabValue] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!resultImport) return;
    const blob = new Blob([resultImport]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_device_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);

    // Close modal and reset state after download
    setTimeout(() => {
      onClose();
      setSelectedFile(null);
      setTabValue(0);
    }, 300);

    dispatch(resetResultImport());
  }, [resultImport, dispatch, onClose]);

  const handleSubmit = data => {
    const newDevice = {
      ...data,
      date_of_manufacture: moment(data?.date_of_manufacture).format(
        'yyyy-MM-DD'
      )
    };
    setInitValue(newDevice);
    dispatch(createDevice(newDevice));
    onClose();
  };

  const handleClose = () => {
    onClose();
    setInitValue(initStateFromValue);
    setTabValue(0);
    setSelectedFile(null);
  };

  const handleChoose = (e, value, setFieldValue, type) => {
    if (type === 'device_type_id') {
      setFieldValue(type, Number(value.id));
      getListVersion({ device_type_id: value.id });
    } else {
      setFieldValue(type, value.name);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUploadFile = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile, selectedFile.name);
      dispatch(importDevice(formData));

      // Refresh device list after upload
      if (getListVersion) {
        getListVersion();
      }

      // Don't close modal here - wait for resultImport in useEffect
    }
  };

  return (
    <CustomDialog title="Thêm mới thiết bị" open={open} onClose={handleClose}>
      <div>
        {/* Tabs Header */}
        <AppBar position="static" elevation={0} className={classes.appBar}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="create device tabs"
            className={classes.tabs}
          >
            <Tab label="Thêm thủ công" {...a11yProps(0)} />
            <Tab label="Thêm từ file" {...a11yProps(1)} />
          </Tabs>
        </AppBar>

        {/* Tab 1: Thêm thủ công */}
        <TabPanel value={tabValue} index={0}>
          <Formik
            initialValues={initValue}
            validationSchema={Yup.object().shape({
              serial: Yup.string()
                .max(100)
                .required('Serial không được để trống'),
              device_type_id: Yup.string()
                .max(100)
                .required('Loại thiết bị không được để trống'),
              hardware_version: Yup.string()
                .max(100)
                .required('Phiên bản không được để trống'),
              date_of_manufacture: Yup.string()
                .max(100)
                .required('Ngày sản xuất không được để trống')
            })}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item md={6} xs={12}>
                    <InputLabel>
                      Serial <span style={{ color: 'red' }}>*</span>
                    </InputLabel>
                    <TextField
                      error={Boolean(touched.serial && errors.serial)}
                      className="input-no-fieldset"
                      fullWidth
                      helperText={touched.serial && errors.serial}
                      margin="normal"
                      name="serial"
                      size="small"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.serial}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputLabel style={{ marginBottom: '21px' }}>
                      Loại thiết bị <span className="text-danger">*</span>
                    </InputLabel>
                    <Autocomplete
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      size="small"
                      options={deviceType || []}
                      getOptionLabel={option => option.name}
                      value={(deviceType || []).find(
                        option => option.id === values.device_type_id
                      )}
                      onChange={(e, value) =>
                        handleChoose(e, value, setFieldValue, 'device_type_id')
                      }
                      disableClearable
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Chọn loại thiết bị"
                          name="device_type_id"
                          error={Boolean(
                            touched.device_type_id && errors.device_type_id
                          )}
                          helperText={
                            touched.device_type_id && errors.device_type_id
                          }
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              fontSize: '16px',
                              padding: '0 8px',
                              height: '35px'
                            }
                          }}
                        />
                      )}
                      className={classes.customOutline}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputLabel style={{ marginBottom: '21px' }}>
                      Phiên bản <span style={{ color: 'red' }}>*</span>
                    </InputLabel>
                    <Autocomplete
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      size="small"
                      options={versionOption || []}
                      getOptionLabel={option => option.name}
                      value={(versionOption || []).find(
                        option => option.id === values.hardware_version
                      )}
                      onChange={(e, value) =>
                        handleChoose(e, value, setFieldValue, 'hardware_version')
                      }
                      disableClearable
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Chọn phiên bản"
                          name="hardware_version"
                          error={Boolean(
                            touched.hardware_version && errors.hardware_version
                          )}
                          helperText={
                            touched.hardware_version && errors.hardware_version
                          }
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              fontSize: '16px',
                              fontWeight: '400',
                              padding: '0 8px',
                              height: '35px'
                            }
                          }}
                        />
                      )}
                      className={classes.customOutline}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputLabel>
                      Ngày sản xuất <span style={{ color: 'red' }}></span>
                    </InputLabel>
                    <TextField
                      size="small"
                      fullWidth
                      error={Boolean(
                        touched.date_of_manufacture && errors.date_of_manufacture
                      )}
                      helperText={
                        touched.date_of_manufacture && errors.date_of_manufacture
                      }
                      className="input-no-fieldset"
                      margin="normal"
                      name="date_of_manufacture"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.date_of_manufacture}
                      type="datetime-local"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Box my={3}>
                  <div className={classes.groupButtonSubmit}>
                    <div className={classes.wrapper}>
                      <Button
                        style={{
                          ...(isMobile && { maxWidth: 90 }),
                          marginRight: '10px',
                          textTranform: 'none !important'
                        }}
                        onClick={() => onClose()}
                        className="btn-main btn-plain mx-3"
                        color="primary"
                        size="large"
                        variant="contained"
                      >
                        Thoát
                      </Button>
                      <Button
                        style={{
                          ...(isMobile && { maxWidth: 120 }),
                          marginRight: '10px',
                          textTranform: 'none !important'
                        }}
                        className="btn-main mx-3"
                        color="primary"
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        Thêm mới
                      </Button>
                      {statusCreate === STATUS_API.PENDING && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </div>
                  </div>
                </Box>
              </form>
            )}
          </Formik>
        </TabPanel>

        {/* Tab 2: Thêm từ file */}
        <TabPanel value={tabValue} index={1}>
          <Box
            className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
            />
            <label htmlFor="file-upload" className={classes.uploadLabel}>
              <CloudUploadIcon className={classes.uploadIcon} />
              <Typography variant="h6" className={classes.uploadText}>
                {selectedFile ? selectedFile.name : 'Kéo thả file vào đây hoặc click để chọn file'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Hỗ trợ các file: .xlsx, .xls, .csv
              </Typography>
            </label>
          </Box>

          <Box my={3}>
            <div className={classes.groupButtonSubmit}>
              <div className={classes.wrapper}>
                <Button
                  style={{
                    ...(isMobile && { maxWidth: 90 }),
                    marginRight: '10px',
                    textTranform: 'none !important'
                  }}
                  onClick={() => {
                    onClose();
                    setSelectedFile(null);
                  }}
                  className="btn-main btn-plain mx-3"
                  color="primary"
                  size="large"
                  variant="contained"
                >
                  Thoát
                </Button>
                <Button
                  style={{
                    ...(isMobile && { maxWidth: 120 }),
                    marginRight: '10px',
                    textTranform: 'none !important'
                  }}
                  className="btn-main mx-3"
                  color="primary"
                  size="large"
                  onClick={handleUploadFile}
                  variant="contained"
                  disabled={!selectedFile}
                >
                  Upload
                </Button>
              </div>
            </div>
          </Box>
        </TabPanel>
      </div>
    </CustomDialog>
  );
});

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  appBar: {
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    marginBottom: 10
  },
  tabs: {
    backgroundColor: 'transparent',
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '1rem'
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main
    }
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px',

    '& .left-button': {
      display: 'flex'
    }
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  customOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: 'rgba(31, 31, 31, 0.14)',
      borderRadius: '4px'
    }
  },
  dropZone: {
    border: '2px dashed rgba(0, 0, 0, 0.23)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(6),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.paper,
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(0, 0, 0, 0.02)'
    }
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  },
  uploadLabel: {
    cursor: 'pointer',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  uploadIcon: {
    fontSize: 64,
    color: 'rgba(0, 0, 0, 0.23)',
    marginBottom: theme.spacing(2)
  },
  uploadText: {
    marginBottom: theme.spacing(1),
    fontWeight: 500
  }
}));

export default CreateDevice;
